import React, { useRef, useEffect, useMemo } from "react";
import { Graphics, Text } from "@pixi/react";
import { string2hex } from "@pixi/utils";
import { SCALE_MODES as PIXI_SCALE_MODES } from "pixi.js";
import BorderedSprite from "../BorderedSprite";
import { buildIconMap } from "./constants";

/* -------------------------------------------------------------------------- */
/*                         Helper: Territory Merging                        */
/* -------------------------------------------------------------------------- */

export const getMergedTerritory = (nation) => {
  let territory = { x: [], y: [] };
  if (nation.territory && nation.territory.x && nation.territory.y) {
    territory = {
      x: [...nation.territory.x],
      y: [...nation.territory.y],
    };
  } else if (
    nation.territoryDeltaForClient &&
    nation.territoryDeltaForClient.add
  ) {
    territory = {
      x: [...(nation.territoryDeltaForClient.add.x || [])],
      y: [...(nation.territoryDeltaForClient.add.y || [])],
    };
  }
  if (nation.territoryDeltaForClient && nation.territoryDeltaForClient.sub) {
    const subSet = new Set();
    const subX = nation.territoryDeltaForClient.sub.x;
    const subY = nation.territoryDeltaForClient.sub.y;
    for (let i = 0; i < subX.length; i++) {
      subSet.add((subY[i] << 16) | subX[i]);
    }
    const merged = { x: [], y: [] };
    for (let i = 0; i < territory.x.length; i++) {
      if (!subSet.has((territory.y[i] << 16) | territory.x[i])) {
        merged.x.push(territory.x[i]);
        merged.y.push(territory.y[i]);
      }
    }
    territory = merged;
  }
  return territory;
};

/* -------------------------------------------------------------------------- */
/*                              NationLabel                                  */
/* -------------------------------------------------------------------------- */

export const NationLabel = ({ text, x, y, fontSize, alpha, scale }) => {
  const labelRef = useRef(null);
  useEffect(() => {
    if (labelRef.current?.texture?.baseTexture) {
      labelRef.current.texture.baseTexture.scaleMode = PIXI_SCALE_MODES.LINEAR;
    }
  }, [text]);

  const inverseScale = 1 / Math.max(0.35, scale || 1);

  return (
    <Text
      ref={labelRef}
      text={text}
      x={x}
      y={y}
      anchor={0.5}
      zIndex={130}
      scale={inverseScale}
      resolution={window.devicePixelRatio || 1}
      style={{
        fontFamily: "Barlow Semi Condensed, system-ui",
        fill: "#ffffff",
        fontSize,
        fontWeight: "600",
        stroke: "#000000",
        strokeThickness: Math.max(2, Math.round(fontSize * 0.18)),
      }}
      alpha={alpha}
    />
  );
};

/* -------------------------------------------------------------------------- */
/*                            NationOverlay                                  */
/* -------------------------------------------------------------------------- */

/**
 * NationOverlay
 *
 * This component renders a nation's territory and cities.
 * It memoizes the territory lookup set and border cells so that border
 * detection only happens when the territory actually changes.
 */
export const NationOverlay = React.memo(({
  nation,
  nationIndex,
  cellSize,
  scale,
  userId,
  nationColors,
  ownershipMap,
  visibleBounds,
}) => {
  const palette = [
    "#FF3B30",
    "#34C759",
    "#0A84FF",
    "#FF9F0A",
    "#BF5AF2",
    "#FF375F",
    "#64D2FF",
    "#FFD60A",
    "#32D74B",
    "#5E5CE6",
  ];
  const nationColor =
    nation.color ||
    nationColors?.[nation.owner] ||
    palette[nationIndex % palette.length];
  const baseColor = string2hex(nationColor);

  // Compute the merged territory for this nation.
  const territory = useMemo(() => getMergedTerritory(nation), [nation]);
  const territorySet = useMemo(() => {
    const set = new Set();
    for (let i = 0; i < territory.x.length; i++) {
      set.add((territory.y[i] << 16) | territory.x[i]);
    }
    return set;
  }, [territory]);

  const territoryCentroid = useMemo(() => {
    if (!territory.x.length) return null;
    let sumX = 0;
    let sumY = 0;
    for (let i = 0; i < territory.x.length; i++) {
      sumX += territory.x[i];
      sumY += territory.y[i];
    }
    return {
      x: sumX / territory.x.length,
      y: sumY / territory.y.length,
    };
  }, [territory]);
  const showBorders = scale > 0.35;
  const showCities = scale > 0.4;
  const showLabels = scale > 0.5;

  const labelFontSize = useMemo(() => {
    const population = Math.max(0, Number(nation.population) || 0);
    const popScale = Math.log10(population + 10);
    const base = 14;
    const size = base + popScale * 7;
    return Math.min(44, Math.max(16, size));
  }, [nation.population]);

  const rowRuns = useMemo(() => {
    const rows = new Map();
    const minX = visibleBounds?.minX ?? -Infinity;
    const maxX = visibleBounds?.maxX ?? Infinity;
    const minY = visibleBounds?.minY ?? -Infinity;
    const maxY = visibleBounds?.maxY ?? Infinity;
    const useCulling = scale < 0.7 && visibleBounds;
    for (let i = 0; i < territory.x.length; i++) {
      const x = territory.x[i];
      const y = territory.y[i];
      if (useCulling) {
        if (x < minX || x > maxX || y < minY || y > maxY) continue;
      }
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push(x);
    }
    const runs = [];
    rows.forEach((xs, y) => {
      xs.sort((a, b) => a - b);
      let start = xs[0];
      let prev = xs[0];
      for (let i = 1; i < xs.length; i++) {
        const cur = xs[i];
        if (cur === prev + 1) {
          prev = cur;
          continue;
        }
        runs.push({ y, start, end: prev });
        start = cur;
        prev = cur;
      }
      if (start !== undefined) {
        runs.push({ y, start, end: prev });
      }
    });
    return runs;
  }, [territory, visibleBounds, scale]);

  // Compute which tiles are border tiles (have at least one non-owned neighbor)
  const borderTileSet = useMemo(() => {
    const bSet = new Set();
    if (scale < 0.35) return bSet;
    for (let i = 0; i < territory.x.length; i++) {
      const x = territory.x[i];
      const y = territory.y[i];
      const hasNonOwned =
        !territorySet.has(((y - 1) << 16) | x) ||
        !territorySet.has((y << 16) | (x + 1)) ||
        !territorySet.has(((y + 1) << 16) | x) ||
        !territorySet.has((y << 16) | (x - 1));
      if (hasNonOwned) bSet.add((y << 16) | x);
    }
    return bSet;
  }, [territory, territorySet, scale]);

  return (
    <>
      <Graphics
        key={`territory-${nation.owner}`}
        zIndex={100}
        draw={(g) => {
          g.clear();
          // Interior tiles at 0.6 alpha, edge tiles at full opacity
          for (let i = 0; i < rowRuns.length; i++) {
            const run = rowRuns[i];
            if (!showBorders) {
              // Not zoomed in enough for borders — draw whole run at 0.6
              const width = (run.end - run.start + 1) * cellSize;
              g.beginFill(baseColor, 0.6);
              g.drawRect(run.start * cellSize, run.y * cellSize, width, cellSize);
              g.endFill();
            } else {
              // Split run into edge vs interior segments
              let segStart = run.start;
              let segIsEdge = borderTileSet.has((run.y << 16) | run.start);
              for (let x = run.start + 1; x <= run.end; x++) {
                const isEdge = borderTileSet.has((run.y << 16) | x);
                if (isEdge !== segIsEdge) {
                  const width = (x - segStart) * cellSize;
                  g.beginFill(baseColor, segIsEdge ? 1.0 : 0.5);
                  g.drawRect(segStart * cellSize, run.y * cellSize, width, cellSize);
                  g.endFill();
                  segStart = x;
                  segIsEdge = isEdge;
                }
              }
              // Final segment
              const width = (run.end - segStart + 1) * cellSize;
              g.beginFill(baseColor, segIsEdge ? 1.0 : 0.5);
              g.drawRect(segStart * cellSize, run.y * cellSize, width, cellSize);
              g.endFill();
            }
          }
        }}
      />
      {showLabels && territoryCentroid && (
        <NationLabel
          text={nation.nationName || nation.displayName || nation.owner}
          x={territoryCentroid.x * cellSize}
          y={territoryCentroid.y * cellSize}
          fontSize={labelFontSize}
          alpha={scale > 3.5 ? 0.2 : 0.9}
          scale={scale}
        />
      )}
      {showCities &&
        (nation.cities || []).map((city, idx) => {
          const iconSize = cellSize;
          const centerX = city.x * cellSize + cellSize / 2;
          const centerY = city.y * cellSize + cellSize / 2;
          const showName = scale > 1.5 && city.name;
          return (
            <React.Fragment key={`city-${nation.owner}-${idx}`}>
              <BorderedSprite
                texture={buildIconMap[city.type] || `/${city.type.toLowerCase().replace(" ", "_")}.png`}
                x={centerX}
                y={centerY}
                width={iconSize}
                height={iconSize}
                borderColor={baseColor}
                borderWidth={2 * Math.sqrt(scale)}
                interactive={true}
                pointerdown={(e) => {
                  e.stopPropagation();
                  console.log("City clicked:", city);
                }}
                baseZ={150 + centerY}
              />
              {showName && (
                <NationLabel
                  text={city.type === "capital" ? `${city.name} (Capital)` : city.name}
                  x={centerX}
                  y={centerY + iconSize * 0.6}
                  fontSize={Math.max(12, labelFontSize * 0.75)}
                  alpha={0.9}
                  scale={scale}
                />
              )}
            </React.Fragment>
          );
        })}
    </>
  );
});
